import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  AdminInstallationType,
  UpdateInstallationTypeNewImageBody,
  UpdateInstallationTypeRemoveImageBody,
} from "../../../../../http/vertigrip/types/installationTypes";
import { useInstallationTypesApi } from "../../../../../http/vertigrip/useInstallationTypes";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Textarea from "../../../../ui/Textarea";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface UpdateInstallationTypeForm {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  visibility: VisibilityScopeValue;
}

interface InstallationTypeDetailsProps {
  installationType?: AdminInstallationType;
  disabled?: boolean;
}

const InstallationTypeDetails: FC<InstallationTypeDetailsProps> = ({
  installationType,
  disabled,
}: InstallationTypeDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const installationTypeApi = useInstallationTypesApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: installationType?.image,
    isLoading: !installationType,
    disabled,
  });

  const defaultValues = {
    name: installationType?.name,
    nameIt: installationType?.nameIt ?? null,
    nameDe: installationType?.nameDe ?? null,
    nameEs: installationType?.nameEs ?? null,
    nameFr: installationType?.nameFr ?? null,
    description: installationType?.description,
    descriptionIt: installationType?.descriptionIt ?? null,
    descriptionDe: installationType?.descriptionDe ?? null,
    descriptionEs: installationType?.descriptionEs ?? null,
    descriptionFr: installationType?.descriptionFr ?? null,
    order: installationType?.order ?? 50,
    visibility: installationType?.visibility,
  };

  const submit = async (body: UpdateInstallationTypeNewImageBody | UpdateInstallationTypeRemoveImageBody) => {
    try {
      await installationTypeApi.updateAdminInstallationType(installationType?.id!, body);
      onSave();
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => async (formData: UpdateInstallationTypeForm) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name?.trim()!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      description: formData.description!,
      descriptionIt: formData.descriptionIt?.trim() ?? null,
      descriptionDe: formData.descriptionDe?.trim() ?? null,
      descriptionEs: formData.descriptionEs?.trim() ?? null,
      descriptionFr: formData.descriptionFr?.trim() ?? null,
      visibility: formData.visibility,
    };

    if (newImage) {
      try {
        const signedUrl = await installationTypeApi.generateUploadImageUrl(
          installationType?.id!,
          newImage.name,
          newImage.type,
        );
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateInstallationTypeNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateInstallationTypeRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateInstallationTypeForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [installationType]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
          register("descriptionIt");
          register("descriptionDe");
          register("descriptionEs");
          register("descriptionFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="name"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <FormField label={i18n.translation.common.description} error={errors.description}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="description"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.description}
                    dialogType="textarea"
                  >
                    <Textarea
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("description", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default InstallationTypeDetails;
